import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { deveTentarDeNovo, ehFalhaPassageira, esperaDaTentativa } from '@/lib/falha-de-rede';
import { ehIdPerfil, type IdPerfil } from '@/modulos/acesso/perfis';

import { apiDeSessao, SemSessao, type SessaoDaApi, type InicioDoApp, type ParceiroDaSessao, type TravaDoLancamento } from './api';
import { nomeDoPapelNaTela, type ContaDeSuporte } from './conta-de-suporte';
import { esquecerAoSair } from './lembranca';
import { guardarSessao } from './memoria';

/**
 * Quem está usando o sistema — agora de verdade.
 *
 * ## O que mudou, e por que o arquivo inteiro mudou junto
 *
 * Até esta rodada não havia sessão: o nome vinha da fixture, o papel vinha de
 * uma persona escolhida num seletor, e nada disso chegava ao servidor. A
 * consequência não era cosmética — sem cookie, toda rota de domínio respondia
 * 401 e cada módulo do sprint caía numa massa de demonstração para ter o que
 * mostrar.
 *
 * Agora o nome, a empresa e os papéis vêm de `GET /api/sessao/atual`, que os
 * lê do banco dentro da transação da própria pessoa. Não há mais fixture de
 * usuário, não há persona, e o que a navegação mostra passou a ser
 * consequência do papel que a pessoa tem cadastrado.
 *
 * ## O papel filtra o menu e continua não protegendo nada
 *
 * O código do papel (`gestor-operacional`, `almoxarifado`, …) casa com
 * `IdPerfil` de `modulos/acesso/perfis.ts`, e é ele que estreita a navegação.
 * Isso é conveniência de tela, não autorização: quem digitar o endereço de um
 * módulo entra, e o servidor não confere papel nenhum. A tela de governança
 * diz isso por escrito, e a matriz de permissões continua sendo do dono.
 */

export interface UsuarioDaSessao {
  readonly usuarioId: string;
  readonly nome: string;
  readonly email: string | null;
  readonly empresa: string;
  readonly papeis: readonly { readonly codigo: string; readonly nome: string; readonly filial?: string | null }[];
  /**
   * O primeiro papel que casa com um perfil conhecido — é ele que a navegação
   * usa. `null` quando a pessoa não tem papel, ou tem um papel que o mapa de
   * perfis não conhece: aí o menu volta a ser o mapa inteiro, que é a resposta
   * honesta para "não sei o que mostrar" num sistema que não barra nada.
   */
  readonly perfil: IdPerfil | null;
  /** O NOME do papel que recorta o menu. A barra superior escreve este. */
  readonly perfilNome: string | null;
  /**
   * O QUE ESTA PESSOA PODE FAZER, pelas linhas de `org.papel_permissao`.
   *
   * E ela que recorta a navegacao agora, e nao mais o `perfil` acima. A
   * diferenca e a que separa conveniencia de tela de controle de acesso: o
   * `perfil` levava a proposta de `modulos/acesso/proposta.ts`, que e uma
   * derivacao da secao 1.4 do guia, enquanto o servidor barra pelas linhas do
   * banco. Duas derivacoes da mesma coisa divergem na primeira concessao que
   * alguem alterar pela tela de governanca — e o sintoma e item de menu que
   * abre e responde 403.
   */
  readonly permissoes: readonly string[];
  /** Sessão de suporte da plataforma (decisão 26): só leitura. */
  readonly suporte?: boolean;
  /** O acesso cobre só parte da empresa: as telas da empresa inteira saem do menu (`acesso/alcance.ts`). */
  readonly alcanceParcial: boolean;
  /**
   * 0128 · A trava do lançamento de abastecimento, ou `null` sem trava. Quem
   * a lê é `combustivel/trava-do-lancamento.ts`, e não cada tela.
   */
  readonly travaDoLancamento: TravaDoLancamento | null;
  /** 0130 · O início do app escolhido pelo papel; ausente é o painel (`padrao`). */
  readonly inicioDoApp?: InicioDoApp;
  /** 0132 · Sessão de posto parceiro: só o lançamento no posto do fornecedor. */
  readonly parceiro?: ParceiroDaSessao;
  /** 0134 · A mesma pessoa na administração da plataforma: o vínculo e a senha unificada. */
  readonly contaDeSuporte?: ContaDeSuporte;
}

export const CHAVE_DA_SESSAO = ['sessao'] as const;

function converter(s: SessaoDaApi): UsuarioDaSessao {
  /* O papel que RECORTA O MENU, e o nome dele.
   *
   * A barra superior escrevia `papeis[0].nome` (o primeiro em ordem
   * alfabetica, como o SQL ordena) e a navegacao usava o primeiro papel cujo
   * codigo e um perfil conhecido. Com dois papeis — 'Auxiliar de campo'
   * (codigo desconhecido) e 'Gestor operacional' — a barra dizia uma coisa e o
   * menu mostrava outra, e nada na tela explicava a diferenca.
   *
   * Agora sai daqui UM par (codigo + nome), e as duas telas leem o mesmo. */
  const doMenu = s.papeis.find((p) => ehIdPerfil(p.codigo)) ?? null;
  return {
    usuarioId: s.usuarioId,
    nome: s.nome,
    email: s.email,
    empresa: s.empresa,
    papeis: s.papeis.map((p) => ({ codigo: p.codigo, nome: p.nome, filial: p.filial ?? null })),
    permissoes: s.permissoes,
    perfil: doMenu !== null && ehIdPerfil(doMenu.codigo) ? doMenu.codigo : null,
    /* A conta de suporte (0134) se escreve "Suporte", com o papel que tem. */
    perfilNome: nomeDoPapelNaTela(s.contaDeSuporte, doMenu?.nome ?? null),
    ...(s.suporte === true ? { suporte: true } : {}),
    alcanceParcial: s.alcanceParcial === true,
    travaDoLancamento: s.travaDoLancamento ?? null,
    ...(s.inicioDoApp && s.inicioDoApp !== 'padrao' ? { inicioDoApp: s.inicioDoApp } : {}),
    ...(s.parceiro ? { parceiro: { fornecedorId: s.parceiro.fornecedorId, fornecedor: s.parceiro.fornecedor } } : {}),
    ...(s.contaDeSuporte ? { contaDeSuporte: { vinculada: s.contaDeSuporte.vinculada, unificada: s.contaDeSuporte.unificada } } : {}),
  };
}

/**
 * A sessão, e o estado em que ela pode estar.
 *
 * `null` é resposta, não falha: quer dizer "ninguém entrou ainda". Por isso a
 * consulta não tenta de novo em cima de `SemSessao` — repetir três vezes uma
 * pergunta cuja resposta é "não" só atrasa a tela de login.
 */
export function useSessao() {
  return useQuery<UsuarioDaSessao | null>({
    queryKey: CHAVE_DA_SESSAO,
    queryFn: async () => {
      try {
        const sessao = converter(await apiDeSessao.atual());
        guardarSessao(sessao);
        return sessao;
      } catch (erro) {
        if (erro instanceof SemSessao) {
          guardarSessao(null);
          return null;
        }
        throw erro;
      }
    },
    /* A falha passageira (a API reiniciando numa implantacao) tenta de novo
       com espera crescente antes de a casca mostrar o recado (item 3). */
    retry: (tentativas, erro) => !(erro instanceof SemSessao) && (ehFalhaPassageira(erro) ? deveTentarDeNovo(tentativas, erro) : tentativas < 2),
    retryDelay: esperaDaTentativa,
    staleTime: 60_000,
    /* A FALHA DA SESSAO NAO VIRA TOAST. Quem depende dela desenha o estado
       inteiro, com o que fazer (a casca da mesa e a do celular), e quem so a
       consulta de passagem (`ProvedorDeHistorias`, montado em TODA rota) nao
       tem o que dizer. Sem isto, o `/campo` sem rede — o normal na obra —
       abria com "Nao foi feito. O sistema nao respondeu" cobrindo o
       "Encerrar turno" (robos no QAS, 20/10/2026). */
    meta: { silenciarFalha: true },
  });
}

/**
 * A sessao conferida EM SILENCIO, fora do `useSessao`: para o `/campo`, que
 * abre sem sessao de proposito e so quer saber se ha alguem para mandar o
 * turno e atualizar o pacote. Sem rede ou sem sessao, `null`, sem aviso. Com
 * sessao, ela vai para a memoria (`guardarSessao`) — e e de la que a fila de
 * envio sabe de quem e o lancamento.
 */
export async function sessaoQuieta(): Promise<UsuarioDaSessao | null> {
  try {
    const sessao = converter(await apiDeSessao.atual());
    guardarSessao(sessao);
    return sessao;
  } catch (erro) {
    if (erro instanceof SemSessao) guardarSessao(null);
    return null;
  }
}

/**
 * Entrar.
 *
 * Depois de entrar, o cache inteiro é descartado — e não só a chave da sessão.
 * Cada consulta do sistema é recortada pela empresa de quem perguntou: manter
 * a lista da frota de quem saiu enquanto outra pessoa entra mostraria dado da
 * sessão anterior por alguns instantes, e "alguns instantes" é tempo bastante
 * para alguém ler.
 */
export function useEntrar(opcoes: { readonly silenciarFalha?: boolean } = {}) {
  const cliente = useQueryClient();
  return useMutation({
    /* Entrar nao e "registro gravado": a tela seguinte ja e o aviso de que
       deu certo. Sem o `silencioso`, o toast padrao das escritas aparecia
       sobre o painel recem-aberto dizendo "Registro gravado." */
    meta: { silencioso: true, ...(opcoes.silenciarFalha ? { silenciarFalha: true } : {}) },
    mutationFn: ({ email, senha, manterConectado }: { email: string; senha: string; manterConectado?: boolean }) =>
      apiDeSessao.entrar(email, senha, manterConectado),
    onSuccess: async (resultado) => {
      /* Com o segundo fator (0085) a sessao ainda nao abriu: o cache fica. */
      if (!resultado.entrou) return;
      cliente.clear();
      await cliente.invalidateQueries();
    },
  });
}

/**
 * A etapa do codigo do segundo fator (0085). Entrou, o cache e descartado como
 * na entrada pela senha.
 */
export function useConfirmarCodigo(opcoes: { readonly silenciarFalha?: boolean } = {}) {
  const cliente = useQueryClient();
  return useMutation({
    meta: { silencioso: true, ...(opcoes.silenciarFalha ? { silenciarFalha: true } : {}) },
    mutationFn: ({ pendente, codigo, manterConectado }: { pendente: string; codigo: string; manterConectado?: boolean }) =>
      apiDeSessao.confirmarCodigo(pendente, codigo, manterConectado),
    onSuccess: async () => {
      cliente.clear();
      await cliente.invalidateQueries();
    },
  });
}

/** A etapa do codigo venceu (401): a saida e voltar a senha. */
export function etapaDoCodigoVenceu(erro: unknown): boolean {
  return erro !== null && typeof erro === 'object' && 'status' in erro && (erro as { status: unknown }).status === 401;
}

/** Sair. Mesmo descarte de cache, pelo mesmo motivo — aqui é ainda mais óbvio. */
export function useSair() {
  const cliente = useQueryClient();
  return useMutation({
    /* Sair tambem nao e registro gravado: a tela de entrada e o aviso. */
    meta: { silencioso: true },
    mutationFn: () => apiDeSessao.sair(),
    onSuccess: async () => {
      guardarSessao(null);
      cliente.clear();
      /* O navegador deixa de entregar a senha sem a pessoa escolher, e o
         e-mail so fica com "Lembrar meus dados" ligado (`lembranca.ts`). */
      await esquecerAoSair();
    },
  });
}

/** A mensagem de uma recusa, sempre em duas partes: o que houve e o que fazer. */
export function explicarFalhaDeEntrada(erro: unknown): {
  readonly erro: string;
  readonly comoResolver: string;
} {
  if (erro !== null && typeof erro === 'object' && 'comoResolver' in erro) {
    const recusa = erro as { message?: string; comoResolver?: string };
    return {
      erro: recusa.message ?? 'não foi possível entrar',
      comoResolver: recusa.comoResolver ?? 'Confira o e-mail e a senha e tente outra vez.',
    };
  }
  return {
    erro: 'o sistema não respondeu',
    comoResolver: 'Confira se o servidor está no ar e tente outra vez.',
  };
}
